import React from 'react';
import _invoke from 'lodash/invoke';
import _pick from 'lodash/pick';
import _omit from 'lodash/omit';
import Storage from '../utils/localforage';

export const LOCAL_FORAGE_KEY = 'tabs-router';

/**
 * Use it as React hook on the page where Tabs work with Router.
 * Example:
 * const MyPage = (props) => {
 *   const [value, setValue, returnToPrevPage] = useTabsRouter({ tabs, defaultTabIndex: 1, history, match, location });
 *
 *   ... your code here
 *
 *   return (
 *    <div>My Page</div>
 *   );
 * };
 *
 * tabs: [{
 *   label: 'overview',
 *   content: 'Overview',
 *   route: '/overview',
 *   disabled: true,
 * },
 * {
 *   label: 'groups',
 *   content: <GroupsPage />,
 *   route: '/groups',
 *   disabled: false,
 * },
 *  ...],
 *
 * defaultTabIndex: (number - start from 0)
 * returnToPrevPage - navigates user back to previous page regardless how many tabs navigations are occurred
 */
export const useTabsRouter = ({
  tabs,
  defaultTabIndex,
  history,
  match,
  location,
  skipStateMemoization,
  keepInitialLocationState = false,
}) => {
  const tabsState = React.useRef(null);

  if (tabsState.current === null) {
    try {
      Storage.TABS_ROUTER.getItem(LOCAL_FORAGE_KEY).then((tab) => {
        tabsState.current = tab || {};
      });
    } catch (e) {
      tabsState.current = {};
    }
  }

  const [tabValue, setTabValue] = React.useState(() => {
    // need it for page reload
    const path = location.pathname || '';
    let tabIndex = tabs.findIndex((t) => path.includes(t.route));
    if (tabIndex === -1) {
      tabIndex = defaultTabIndex;
    }
    return tabIndex;
  });

  const returnToPrevPage = () => history.goBack();

  React.useEffect(() => {
    // change tab on location change (ex. back/forward browser buttons)
    const path = location.pathname || '';
    const tabIndex = tabs.findIndex((t) => path.includes(t.route));

    if (tabIndex !== tabValue && tabIndex !== -1) {
      setTabValue(tabIndex);
    }
  }, [location.pathname]);

  React.useEffect(() => {
    // change location on tab change
    if (tabValue === -1) {
      return;
    }

    const currentPath = location.pathname;
    const newPath = `${match?.url}${tabs[tabValue].route}`;

    if (currentPath === newPath) {
      return;
    }

    const currentTab = tabs.find((tab) => currentPath?.includes(tab.route));

    // notify component that is rendered within current that it will be unmounted
    _invoke(currentTab, ['tabRef', 'current', 'onTabLeave']);

    let nextTabState = {};

    // Prevent lost of initial location state that was provided by history.push with state argument
    if (keepInitialLocationState) {
      const { search, state } = _pick(history.location, ['search', 'state']);
      nextTabState = {
        search,
        state: {
          // remove restoreScrollPosition as it's redundant on tabs change and can cause unhandled errors
          ..._omit(state, 'restoreScrollPosition'),
        },
      };
    }

    if (!skipStateMemoization) {
      // retrieve tab state for next transition tab in case it was visited (cached) before
      // Also we need to removed retrieved state from storage since it's gonna be passed to location state
      if (tabsState.current[newPath]) {
        nextTabState = tabsState.current[newPath];

        delete tabsState.current[newPath];
      }

      // save location search and state for current path later it can be restored if the path will match previously saved
      tabsState.current[currentPath] = _pick(history.location, ['search', 'state']);

      Storage.TABS_ROUTER.setItem(LOCAL_FORAGE_KEY, JSON.stringify(tabsState.current));
    }

    history.replace({
      pathname: newPath,
      ..._pick(nextTabState, ['search', 'state']),
    });

    return () => {
      // clear the cached tabs data as soon current page is left
      Storage.TABS_ROUTER.clear();
    };
  }, [tabValue]);

  return [tabValue, setTabValue, returnToPrevPage];
};

export default useTabsRouter;
